// src/pages/Clinic/ClinicPlansPage.jsx
import React, { useEffect, useState, useMemo } from 'react';
import axiosInstance from '../../axiosInstance';
import { useClinic } from '../../context/ClinicContext';
import {
  Box,
  Container,
  Grid,
  Chip,
  Stack,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Card,
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  alpha,
  Tooltip,
} from '@mui/material';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoIcon from '@mui/icons-material/Info';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AccessAlarmsIcon from '@mui/icons-material/AccessAlarms';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SecurityIcon from '@mui/icons-material/Security';
import GroupsIcon from '@mui/icons-material/Groups';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import BadgeIcon from '@mui/icons-material/Badge';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import StarIcon from '@mui/icons-material/Star';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { motion, AnimatePresence } from 'framer-motion';

const MotionBox = motion(Box);
const MotionCard = motion(Card);

// Textos de diferencias por plan
const DIFFERENCES_MAP = {
  'Plan Clínica/Organización Básico': [
    'Ideal para clínicas pequeñas que están iniciando.',
    'Reportes básicos, sin analítica avanzada.',
    'Soporte estándar por email.',
  ],
  'Plan Clínica/Organización Premium': [
    'Incluye reportes avanzados y paneles de control.',
    'Permite recordatorios automáticos a pacientes.',
    'Acceso a finanzas para todo el equipo médico.',
    'Soporte prioritario con tiempo de respuesta reducido.',
  ],
  'Plan Clínica/Organización Anual': [
    'Ahorro anual frente al pago mensual (hasta 25% de descuento).',
    'Capacidad ampliada (límites altos o ilimitados).',
    'Incluye todas las características premium.',
    'Soporte preferente 24/7 y roadmap prioritario.',
  ],
};

// Detecta si un plan es Premium o Anual por nombre
const isPremium = (planName) => {
  const n = (planName || '').toLowerCase();
  return n.includes('premium');
};

const isAnual = (planName) => {
  const n = (planName || '').toLowerCase();
  return n.includes('anual');
};

// Construye listado de features incluidas según banderas del plan
const buildFeatureList = (plan) => {
  const features = [];
  const planName = plan.nombre || '';

  // Para Premium y Anual, FORZAR todas las banderas a true si el backend no las trae
  const isPremiumOrAnual = isPremium(planName) || isAnual(planName);

  // Banderas de permisos (forzar a true si es Premium/Anual)
  features.push({
    label: 'Acceso a Finanzas',
    enabled: isPremiumOrAnual ? true : !!plan.permite_acceso_finanzas,
    icon: <AttachMoneyIcon color={(isPremiumOrAnual || plan.permite_acceso_finanzas) ? 'success' : 'disabled'} />,
  });
  features.push({
    label: 'Recordatorios Automáticos',
    enabled: isPremiumOrAnual ? true : !!plan.permite_recordatorios,
    icon: <AccessAlarmsIcon color={(isPremiumOrAnual || plan.permite_recordatorios) ? 'success' : 'disabled'} />,
  });
  features.push({
    label: 'Reportes Avanzados',
    enabled: isPremiumOrAnual ? true : !!plan.permite_reportes_avanzados,
    icon: <AssessmentIcon color={(isPremiumOrAnual || plan.permite_reportes_avanzados) ? 'success' : 'disabled'} />,
  });

  // Soporte condicional (Premium → Soporte prioritario; Anual → Soporte 24/7)
  if (isAnual(planName)) {
    features.push({
      label: 'Soporte 24/7 preferente',
      enabled: true,
      icon: <SupportAgentIcon color="success" />,
    });
  } else if (isPremium(planName)) {
    features.push({
      label: 'Soporte prioritario',
      enabled: true,
      icon: <SupportAgentIcon color="info" />,
    });
  } else {
    features.push({
      label: 'Soporte estándar por email',
      enabled: true,
      icon: <SupportAgentIcon color="action" />,
    });
  }

  // Frecuencia y recurrencia
  if (plan.es_recurrente) {
    const freq = (plan.frecuencia_facturacion || '').toUpperCase();
    features.push({
      label: `Renovación automática (${freq || '—'})`,
      enabled: true,
      icon: <AutorenewIcon color="info" />,
    });
  } else {
    features.push({
      label: 'Pago único (no recurrente)',
      enabled: true,
      icon: <SecurityIcon color="action" />,
    });
  }

  // Duración
  features.push({
    label: `Duración: ${plan.duracion_dias} días`,
    enabled: true,
    icon: <CalendarMonthIcon color="action" />,
  });

  return features;
};

const LimitItem = ({ icon, label, value }) => (
  <Stack direction="row" alignItems="center" spacing={1}>
    {icon}
    <Typography variant="body2">
      {label}: <b>{value ?? 'Ilimitado'}</b>
    </Typography>
  </Stack>
);

const FeatureList = ({ plan }) => {
  const list = buildFeatureList(plan);

  return (
    <List dense sx={{ mt: 1 }}>
      {list.map((f, idx) => (
        <ListItem key={`$${plan.id}-feature-${idx}`} sx={{ py: 0.5 }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            {f.enabled ? <CheckIcon color="success" /> : <CancelIcon color="disabled" />}
          </ListItemIcon>
          <ListItemText
            primary={
              <Stack direction="row" alignItems="center" spacing={1}>
                {f.icon}
                <Typography variant="body2">{f.label}</Typography>
              </Stack>
            }
          />
        </ListItem>
      ))}
    </List>
  );
};

const DifferencesBlock = ({ plan }) => {
  const diffs = useMemo(() => {
    const byName = DIFFERENCES_MAP[plan?.nombre?.trim()] || [];
    if (byName.length) return byName;
    return [
      'Incluye herramientas esenciales para gestión clínica.',
      'Permite configurar miembros y roles.',
      'Compatibilidad con telemedicina y receta electrónica (según plan).',
    ];
  }, [plan?.nombre]);

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        Diferencias y beneficios
      </Typography>
      <List dense sx={{ mb: 0 }}>
        {diffs.map((d, idx) => (
          <ListItem key={`$${plan.id}-diff-${idx}`} sx={{ py: 0.25 }}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <StarIcon fontSize="small" color="warning" />
            </ListItemIcon>
            <ListItemText primary={<Typography variant="body2">{d}</Typography>} />
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

const PlanCard = ({ plan, onSubscribe, disabled, delay = 0 }) => {
  const theme = useTheme();
  const price = parseFloat(plan.precio || 0);
  const planIsAnual = isAnual(plan.nombre);
  const planIsPremium = isPremium(plan.nombre);

  const showBadge = planIsPremium || planIsAnual;
  const badgeContent = planIsAnual ? 'Ahorra 25%' : planIsPremium ? 'Más Popular' : '';
  const badgeColor = planIsAnual ? 'error' : 'secondary';

  return (
    <MotionCard
      initial={{ opacity: 0, y: 20, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.6, delay }}
      elevation={0}
      sx={{
        borderRadius: 4,
        overflow: 'visible',
        position: 'relative',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, ${alpha(
          theme.palette.secondary.main,
          0.05
        )} 100%)`,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.08)}`,
        '&:hover': {
          boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.14)}`,
          transform: 'translateY(-4px)',
          transition: 'all 0.25s ease',
        },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {showBadge && (
        <Chip
          icon={planIsAnual ? <LocalOfferIcon /> : <WorkspacePremiumIcon />}
          label={badgeContent}
          color={badgeColor}
          size="small"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 10,
            fontWeight: 800,
            fontSize: '0.7rem',
            height: 28,
            boxShadow: `0 4px 14px ${alpha(theme.palette[badgeColor].main, 0.35)}`,
            animation: 'pulse 2s ease-in-out infinite',
            '@keyframes pulse': {
              '0%, 100%': { transform: 'scale(1)' },
              '50%': { transform: 'scale(1.05)' },
            },
          }}
        />
      )}

      <CardContent sx={{ p: 3, flexGrow: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
          <InfoIcon color="primary" sx={{ opacity: 0.8 }} />
          <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 1 }}>
            Plan Clínica | Organización
          </Typography>
        </Stack>

        <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
          {plan.nombre}
        </Typography>

        <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1.5 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 900,
              color: 'primary.main',
              textShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.15)}`,
            }}
          >
            ${price.toFixed(2)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            /mes
          </Typography>
        </Stack>

        <Typography variant="body2" color="text.secondary">
          Duración: {plan.duracion_dias} días
        </Typography>

        {/* Límites por rol */}
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 3,
            background: `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(
              theme.palette.background.paper,
              0.9
            )} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
            Límites por rol
          </Typography>
          <Stack spacing={0.5}>
            <LimitItem
              icon={<LocalHospitalIcon fontSize="small" color="action" />}
              label="Médicos"
              value={plan.limite_medicos ?? 'Ilimitado'}
            />
            <LimitItem
              icon={<BadgeIcon fontSize="small" color="action" />}
              label="Enfermería"
              value={plan.limite_enfermeria ?? 'Ilimitado'}
            />
            <LimitItem
              icon={<GroupsIcon fontSize="small" color="action" />}
              label="Staff"
              value={plan.limite_staff ?? 'Ilimitado'}
            />
          </Stack>
        </Box>

        <FeatureList plan={plan} />

        {plan.es_recurrente && (
          <Chip
            icon={<CheckCircleIcon />}
            label={`Renovación automática (${(plan.frecuencia_facturacion || '').toUpperCase()})`}
            size="small"
            sx={{
              mt: 1.5,
              bgcolor: alpha(theme.palette.success.main, 0.15),
              color: 'success.dark',
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
              fontWeight: 700,
            }}
          />
        )}

        <DifferencesBlock plan={plan} />
      </CardContent>

      <Divider sx={{ mx: 3 }} />

      <CardActions sx={{ px: 3, pb: 3, pt: 2 }}>
        <Tooltip title={!disabled ? '' : 'Selecciona una clínica en el selector superior'}>
          <span style={{ width: '100%' }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<MonetizationOnIcon />}
              onClick={onSubscribe}
              disabled={disabled}
              sx={{
                py: 1.3,
                borderRadius: 3,
                fontWeight: 700,
                bgcolor: 'primary.main',
                boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.3)}`,
                '&:hover': {
                  bgcolor: 'primary.dark',
                  transform: 'translateY(-2px)',
                  boxShadow: `0 10px 28px ${alpha(theme.palette.primary.main, 0.45)}`,
                },
                transition: 'all 0.25s ease',
              }}
            >
              {disabled ? 'Selecciona clínica' : 'Suscribirme'}
            </Button>
          </span>
        </Tooltip>
      </CardActions>
    </MotionCard>
  );
};

const ClinicPlansPage = () => {
  const theme = useTheme();
  const { selectedClinicId, activeClinic } = useClinic();
  const [planes, setPlanes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await axiosInstance.get('/clinica/planes/', { signal: controller.signal });
        setPlanes(Array.isArray(data) ? data : data?.results ?? []);
      } catch (e) {
        console.error('[ClinicPlansPage] Error /clinica/planes', e?.response?.status, e?.response?.data);
        setError('No se pudieron cargar los planes de clínica');
      } finally {
        setLoading(false);
      }
    })();
    return () => controller.abort();
  }, []);

  const iniciarPago = async (planId) => {
    try {
      if (!selectedClinicId) return alert('Selecciona una clínica primero');
      const { data } = await axiosInstance.post('/clinica/suscripcion/iniciar-pago/', {
        clinic_id: selectedClinicId,
        plan_id: planId,
        es_recurrente: true,
      });
      if (data?.process_url) {
        window.open(data.process_url, '_blank', 'noopener,noreferrer');
      } else {
        alert('No se pudo iniciar el proceso de pago');
      }
    } catch (e) {
      console.error('Error iniciar pago clínica:', e?.response?.data || e);
      alert('Error al iniciar el pago');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(
          theme.palette.secondary.main,
          0.05
        )} 100%)`,
        py: 6,
      }}
    >
      <Container maxWidth="xl">
        <AnimatePresence mode="wait">
          <MotionBox
            key="header"
            initial={{ opacity: 0, y: -14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            sx={{
              mb: 4,
              p: 4,
              borderRadius: 6,
              color: 'white',
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              boxShadow: `0 8px 28px ${alpha('#000', 0.2)}`,
            }}
          >
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: 0.2 }}>
                  Planes para Clínicas
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Selecciona el plan que mejor se adapte a tu organización.
                </Typography>
              </Box>
              {activeClinic && (
                <Chip
                  label={`Clínica activa: ${activeClinic.clinica?.nombre}`}
                  color="default"
                  sx={{
                    bgcolor: alpha('#fff', 0.15),
                    color: 'white',
                    border: `1px solid ${alpha('#fff', 0.35)}`,
                    fontWeight: 700,
                    height: 36,
                  }}
                />
              )}
            </Stack>
          </MotionBox>

          {!selectedClinicId && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Cambia a un contexto de clínica con el selector de la barra superior para suscribirte.
            </Alert>
          )}

          {loading && (
            <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {!loading && !error && (
            <Grid container spacing={3}>
              {planes.map((p, idx) => (
                <Grid item xs={12} md={6} lg={4} key={p.id}>
                  <PlanCard plan={p} onSubscribe={() => iniciarPago(p.id)} disabled={!selectedClinicId} delay={0.05 * idx} />
                </Grid>
              ))}
              {planes.length === 0 && (
                <Grid item xs={12}>
                  <Alert severity="warning">No hay planes disponibles por el momento.</Alert>
                </Grid>
              )}
            </Grid>
          )}
        </AnimatePresence>
      </Container>
    </Box>
  );
};

export default ClinicPlansPage;
